import type { LimitRow, LimitsModel } from "../app/limits-model.js";
import { el } from "../lib/dom.js";
import { keepTogether } from "../lib/keep.js";
import { icon } from "../lib/icon.js";
import type { AimHandlers } from "./aim.js";
import { loadFailed, setBusy } from "./busy.js";
import { tabBar, type Tab } from "./tabs.js";

/**
 * The Limits page (DCTA-154; UX spec §F.2 21t–21x, DCTA-155), pushed under Me — or over the chat, where a payment's cap
 * refusal points to it ("See my limits") instead of naming a figure. Read only, built from 17's parts: a heading per
 * section, key-value rows (`.list.kv`: the label in `.k`, the value in `.v`, a figure in the money face) that are
 * information, never controls, a help line under each list and the page's last line. States: loading (skeleton rows,
 * hidden from assistive tech, one status line), default, couldn't load (21x: what failed, what that does not touch,
 * Try again — and no figure). Layout: app.css 51.
 */
export interface LimitsHandlers {
  onBack(): void;
  onRetry(): void;
  onTab(tab: Tab): void;
  readonly assistant: AimHandlers;
}

export class LimitsPage {
  readonly parts: readonly HTMLElement[];
  readonly topbar: HTMLElement;
  readonly body: HTMLElement;
  readonly dock: HTMLElement;
  readonly #h: LimitsHandlers;
  readonly #back: HTMLButtonElement;
  readonly #note: HTMLElement;
  readonly #wrap: HTMLElement;

  constructor(handlers: LimitsHandlers) {
    this.#h = handlers;
    this.#back = el("button", { class: "raw icobtn back", type: "button", aria: { label: "Back to Me" } }, [icon("i-chev-l")]);
    this.#back.addEventListener("click", () => this.#h.onBack());
    this.topbar = el("div", { class: "topbar" }, [this.#back, el("span", { class: "t" }, ["Limits"]), el("span", { class: "topbar-pad", aria: { hidden: "true" } })]);
    this.#note = el("span", { class: "vis-hidden loading-note", role: "status" });
    this.#wrap = el("div", { class: "limits" });
    this.body = el("div", { class: "body" }, [this.#note, this.#wrap, el("div", { class: "home-end", aria: { hidden: "true" } })]);
    const bar = tabBar("me", { onTab: (t) => this.#h.onTab(t), ...this.#h.assistant }, { pushed: true });
    this.dock = bar.dock;
    this.parts = [this.topbar, this.body, this.dock];
  }

  /** Where Back goes, as a screen reader hears it: "Back to Me", or "Back to Assistant" over the chat. */
  backTo(label: string): void {
    this.#back.setAttribute("aria-label", label);
  }

  loading(): void {
    setBusy(this.body, this.#note, this.#wrap, true, "Loading your limits");
    this.#wrap.replaceChildren(el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" }));
  }

  show(m: LimitsModel): void {
    setBusy(this.body, this.#note, this.#wrap, false, "");
    this.#wrap.replaceChildren(
      ...m.sections.flatMap((s) => [
        el("div", { class: "sec" }, [el("h3", {}, [s.title])]),
        el("div", { class: "list kv" }, s.rows.map(row)),
        ...(s.help === undefined ? [] : [el("p", { class: "help" }, [s.help])]),
      ]),
      el("p", { class: "help last" }, [m.last]),
    );
  }

  /** The read failed: say so, and that nothing changed; claim no figure. */
  unreachable(): void {
    setBusy(this.body, this.#note, this.#wrap, false, "");
    this.#wrap.replaceChildren(loadFailed("Couldn't load your limits", "Your limits and money are not affected.", () => this.#h.onRetry(), "banner unreach"));
  }
}

/** One row, read label then value ("In one payment, Up to S$50,000.00") — every string as text. */
function row(r: LimitRow): HTMLElement {
  return el("div", { class: `lim-${r.key}` }, [
    el("span", { class: "k" }, [r.label]),
    // P1-6 (DCTA-192): "from 13 Oct 09:09" never breaks inside its date.
    el("span", { class: "v" }, r.value.flatMap((p) => (typeof p === "string" ? keepTogether(p) : [el("span", { class: "money" }, [p.money])]))),
  ]);
}
